import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiDollarSign, FiTrendingUp, FiTrendingDown, FiZap,
  FiMessageSquare, FiMail, FiCalendar, FiCreditCard
} from 'react-icons/fi';
import api from '../api';

export default function Points() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    balance: 0,
    plan: {
      type: 'free',
      monthlyPoints: 0,
      availablePoints: 0,
      quotas: {},
      usage: {},
      resetDate: null
    },
    limits: {},
    monthlyUsage: {
      push: { count: 0, free: 0, paid: 0, cost: 0 },
      alimtalk: { count: 0, free: 0, paid: 0, cost: 0 },
      sms: { count: 0, free: 0, paid: 0, cost: 0 }
    },
    billingMonth: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await api.get('/api/points');
      setData(res.data);
    } catch (error) {
      console.error('Failed to load points:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanName = (type) => {
    const names = {
      free: '무료 플랜',
      basic: '베이직 플랜',
      pro: '프로 플랜'
    };
    return names[type] || type;
  };

  const getPlanColor = (type) => {
    const colors = {
      free: '#6B7280',
      basic: '#3B82F6',
      pro: '#8B5CF6'
    };
    return colors[type] || '#6B7280';
  };

  const totalMonthlyCost =
    data.monthlyUsage.push.cost +
    data.monthlyUsage.alimtalk.cost +
    data.monthlyUsage.sms.cost;

  const totalMonthlyCount =
    data.monthlyUsage.push.count +
    data.monthlyUsage.alimtalk.count +
    data.monthlyUsage.sms.count;

  // 플랜별 현황 카드
  const renderPlanInfo = () => {
    const { plan } = data;
    const planColor = getPlanColor(plan.type);

    return (
      <div className="plan-info-card" style={{ borderColor: planColor }}>
        <div className="plan-header">
          <div>
            <h3 style={{ color: planColor }}>{getPlanName(plan.type)}</h3>
            {plan.resetDate && (
              <p className="reset-date">
                <FiCalendar size={14} />
                다음 갱신일: {format(new Date(plan.resetDate), 'yyyy년 MM월 01일')}
              </p>
            )}
          </div>
          <div className="balance-display">
            <span className="balance-label">현재 잔액</span>
            <span className="balance-value">{data.balance.toLocaleString()}원</span>
          </div>
        </div>

        {/* Basic 플랜: 월간 포인트 표시 */}
        {plan.type === 'basic' && (
          <div className="points-bar">
            <div className="points-info">
              <span>월간 포인트</span>
              <span>{plan.availablePoints.toLocaleString()}P / {plan.monthlyPoints.toLocaleString()}P</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${(plan.availablePoints / plan.monthlyPoints) * 100}%`,
                  backgroundColor: planColor
                }}
              />
            </div>
          </div>
        )}

        {/* Pro 플랜: 할당량 표시 */}
        {plan.type === 'pro' && (
          <div className="quotas-grid">
            <div className="quota-item">
              <FiZap size={20} color={planColor} />
              <div>
                <span className="quota-label">푸시 알림</span>
                <span className="quota-value">무제한</span>
              </div>
            </div>
            <div className="quota-item">
              <FiMessageSquare size={20} color={planColor} />
              <div>
                <span className="quota-label">알림톡</span>
                <span className="quota-value">
                  {plan.usage.alimtalk || 0} / {plan.quotas.alimtalk} 건
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 이번 달 사용 통계
  const usageStats = [
    {
      icon: FiZap,
      label: '푸시 알림',
      count: data.monthlyUsage.push.count,
      free: data.monthlyUsage.push.free,
      paid: data.monthlyUsage.push.paid,
      cost: data.monthlyUsage.push.cost,
      color: '#3B82F6'
    },
    {
      icon: FiMessageSquare,
      label: '알림톡',
      count: data.monthlyUsage.alimtalk.count,
      free: data.monthlyUsage.alimtalk.free,
      paid: data.monthlyUsage.alimtalk.paid,
      cost: data.monthlyUsage.alimtalk.cost,
      color: '#10B981'
    },
    {
      icon: FiMail,
      label: 'SMS',
      count: data.monthlyUsage.sms.count,
      free: data.monthlyUsage.sms.free,
      paid: data.monthlyUsage.sms.paid,
      cost: data.monthlyUsage.sms.cost,
      color: '#F59E0B'
    },
  ];

  return (
    <div className="points-page">
      <div className="page-header">
        <div>
          <h2>포인트 관리</h2>
          <p>포인트 현황 및 사용 내역을 확인하세요</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => navigate('/points/usage')}>
            사용 내역
          </button>
          <button className="btn-primary" onClick={() => navigate('/points/history')}>
            충전/사용 기록
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <>
          {/* 플랜 정보 */}
          {renderPlanInfo()}

          {/* 이번 달 사용 통계 */}
          <div className="section">
            <div className="section-header">
              <h3>이번 달 사용 현황</h3>
              <span className="billing-month">{data.billingMonth}</span>
            </div>

            <div className="usage-stats-grid">
              {usageStats.map((stat, index) => (
                <div key={index} className="usage-stat-card">
                  <div className="stat-icon" style={{ backgroundColor: `${stat.color}20`, color: stat.color }}>
                    <stat.icon size={24} />
                  </div>
                  <div className="stat-details">
                    <span className="stat-label">{stat.label}</span>
                    <span className="stat-count">{stat.count.toLocaleString()}건</span>
                    {(stat.free > 0 || stat.paid > 0) && (
                      <div className="stat-breakdown">
                        {stat.free > 0 && <span className="free">무료 {stat.free}건</span>}
                        {stat.paid > 0 && <span className="paid">유료 {stat.paid}건</span>}
                      </div>
                    )}
                    {stat.cost > 0 && (
                      <span className="stat-cost">{stat.cost.toLocaleString()}원</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* 총 사용 비용 */}
            <div className="total-cost-card">
              <div className="cost-row">
                <span className="cost-label">
                  <FiTrendingDown />
                  이번 달 총 발송
                </span>
                <span className="cost-value">{totalMonthlyCount.toLocaleString()}건</span>
              </div>
              <div className="cost-row">
                <span className="cost-label">
                  <FiDollarSign />
                  이번 달 사용 금액
                </span>
                <span className="cost-value highlight">{totalMonthlyCost.toLocaleString()}원</span>
              </div>
            </div>
          </div>

          {/* 빠른 작업 */}
          <div className="section">
            <h3>빠른 작업</h3>
            <div className="quick-actions">
              <button className="action-btn" onClick={() => navigate('/points/charge')}>
                <FiCreditCard />
                포인트 충전
              </button>
              <button className="action-btn" onClick={() => navigate('/points/usage')}>
                <FiTrendingDown />
                상세 사용 내역
              </button>
              <button className="action-btn" onClick={() => navigate('/settings?tab=plan')}>
                <FiTrendingUp />
                플랜 업그레이드
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
